/**
 * flatNavmesh — the one generator for a regular grid navmesh graph.
 *
 * Two consumers, deliberately sharing it rather than each rolling a grid:
 *   - NavMeshVolume's STUDIO FLOOR: a scene with no environment splat has no
 *     baked navmesh to fetch, but it does have a floor — the studio stage and
 *     its reference grid — so the walkable area is known, not guessed.
 *   - navDev's `__NAV_DEV__.loadFlat()` console harness (which is explicitly not
 *     a product surface, hence this module rather than a component importing it).
 *
 * Pure data, no THREE / no DOM: it emits the same `{ nodes, edges }` shape the
 * backend bake writes, so `new NavMesh(...)` consumes it unchanged.
 */

/** Extent (m) and cell size (m) of the studio floor's navmesh.
 *
 *  These MIRROR the reference grid drawn in Scene.jsx — `gridHelper args={[10,
 *  20, …]}`, i.e. 10 m across in 20 divisions = 0.5 m cells — so the walkable
 *  area is exactly the floor the user can see. Change them together with that
 *  helper or the grid stops meaning what it looks like; the pairing is pinned by
 *  tests/unit/flatNavmesh.test.mjs. */
export const STUDIO_FLOOR_SIZE = 10
export const STUDIO_FLOOR_CELL = 0.5

/**
 * Build a flat 4-connected grid navmesh centred on the origin.
 *
 * @param {object} [o]
 * @param {number} [o.size=8]    extent in metres (the grid spans ±size/2)
 * @param {number} [o.cell=0.5]  cell size in metres
 * @param {number} [o.floorY=0]  world Y every node sits at
 * @param {string} [o.splatUrl]  provenance string carried onto the graph
 * @returns {object} backend-bake-shaped navmesh data
 */
export function buildFlatNavmeshData({ size = 8, cell = 0.5, floorY = 0, splatUrl = 'dev' } = {}) {
  const n = Math.max(2, Math.round(size / cell) + 1)
  const origin = -size / 2
  const idx = (gx, gz) => gz * n + gx
  const nodes = []
  for (let gz = 0; gz < n; gz++)
    for (let gx = 0; gx < n; gx++)
      nodes.push({ i: idx(gx, gz), p: [origin + gx * cell, floorY, origin + gz * cell] })
  const edges = []
  for (let gz = 0; gz < n; gz++)
    for (let gx = 0; gx < n; gx++) {
      if (gx + 1 < n) edges.push({ a: idx(gx, gz), b: idx(gx + 1, gz), cost: cell })
      if (gz + 1 < n) edges.push({ a: idx(gx, gz), b: idx(gx, gz + 1), cost: cell })
    }
  return {
    version: 1, splatUrl, floorY, cellSize: cell,
    bounds: { min: [origin, floorY, origin], max: [origin + (n - 1) * cell, floorY, origin + (n - 1) * cell] },
    nodes, edges,
  }
}

/** The studio floor's graph: the reference grid, walkable. */
export function buildStudioFloorNavmeshData(floorY = 0) {
  return buildFlatNavmeshData({
    size: STUDIO_FLOOR_SIZE,
    cell: STUDIO_FLOOR_CELL,
    floorY,
    splatUrl: 'studio-floor',
  })
}
